import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import GoalButton from "layouts/goals/components/GoalButton";
import ErrorAlert from "components/ErrorAlert";
import useAdminUsers from "./useAdminUsers";
import AdminUsersTable from "./components/AdminUsersTable";
import AdminUserFormModal from "./components/AdminUserFormModal";
import DeleteAdminUserModal from "./components/DeleteAdminUserModal";

function UserManagement() {
  const {
    auth: { admin, admin_teams_channels },
  } = useSelector((state) => state);

  const {
    adminUsers,
    roles,
    isLoading,
    errorMessage,
    successMessage,
    fetchAll,
    createAdminUser,
    updateAdminUser,
    deleteAdminUser,
    clearError,
    clearSuccess,
  } = useAdminUsers(admin?.id);

  const navigate = useNavigate();
  // Roles can only be changed by Super Admins (and platform admins).
  const canManageRoles = !!(admin?.is_super_admin || admin?.is_platform_admin);

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  useEffect(() => {
    fetchAll();
  }, [admin?.id]);

  // An admin can only hand out teams they belong to (the API enforces this too).
  const teamOptions = useMemo(() => {
    const seen = new Map();
    (admin_teams_channels || []).forEach(({ team_id, team_name }) => {
      if (!seen.has(team_id)) seen.set(team_id, { value: team_id, label: team_name });
    });
    return Array.from(seen.values());
  }, [admin_teams_channels]);

  const openAddModal = () => {
    setEditingUser(null);
    setShowFormModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setShowFormModal(true);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    setEditingUser(null);
  };

  const handleSave = async (body) => {
    setIsSubmitting(true);
    const ok = editingUser
      ? await updateAdminUser(editingUser.id, body)
      : await createAdminUser(body);
    setIsSubmitting(false);
    if (ok) closeFormModal();
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    setIsSubmitting(true);
    const ok = await deleteAdminUser(userToDelete.id);
    setIsSubmitting(false);
    if (ok) setUserToDelete(null);
  };

  const handleToggleActive = (user) => updateAdminUser(user.id, { is_active: !user.is_active });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SuiBox py={3}>
        {errorMessage && (
          <ErrorAlert color="error" message={errorMessage} clearError={clearError} />
        )}
        {successMessage && (
          <ErrorAlert color="success" message={successMessage} clearError={clearSuccess} />
        )}
        <SuiBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SuiTypography variant="h5" fontWeight="medium">
            Users
          </SuiTypography>
          <SuiBox display="flex" gap={1.5}>
            {canManageRoles && (
              <GoalButton
                variant="secondary"
                icon="admin_panel_settings"
                onClick={() => navigate("/users/roles")}
              >
                Roles
              </GoalButton>
            )}
            <GoalButton icon="person_add" onClick={openAddModal}>
              Add User
            </GoalButton>
          </SuiBox>
        </SuiBox>
        <AdminUsersTable
          adminUsers={adminUsers}
          currentAdminId={admin?.id}
          onEdit={openEditModal}
          onToggleActive={handleToggleActive}
          onDelete={setUserToDelete}
          isLoading={isLoading}
        />
        <DeleteAdminUserModal
          open={!!userToDelete}
          onClose={() => setUserToDelete(null)}
          onConfirm={handleConfirmDelete}
          userName={userToDelete?.name}
          isSubmitting={isSubmitting}
        />
        <AdminUserFormModal
          open={showFormModal}
          onClose={closeFormModal}
          onSave={handleSave}
          isSubmitting={isSubmitting}
          initialUser={editingUser}
          isSelf={editingUser?.id === admin?.id}
          roles={roles}
          teamOptions={teamOptions}
        />
      </SuiBox>
      <Footer />
    </DashboardLayout>
  );
}

export default UserManagement;
